// src/components/userSettings/VacationBalancesSection.jsx
//
// Annual Leave-Type summaries and corrections
// ===========================================
//
// v10 design decision
// -------------------
// The Leave / HR module uses leave_types as the central master-data dictionary
// for all HR/time events:
// - normal leave that deducts annual leave balance,
// - usage-only events such as business trips,
// - private exits that create work-time debt,
// - make-up work that reduces work-time debt.
//
// The previous v9 UI rendered these groups in separate tables.  The first
// bucket-aware version then tried to combine all values into one wide annual
// table, but the carryover/current-year split made the card too narrow and
// forced too much horizontal scrolling.
//
// Current UX decision:
// - show one compact balance card per Leave Type,
// - keep the most important totals visible immediately,
// - show carryover and current-year entitlement as grouped bucket panels,
// - keep HR corrections close to the affected Leave Type,
// - avoid widening shared page containers or leaking layout changes into other
//   User Settings, admin overview, Dashboard shell or public shell screens.
//
// Leave Types now use policy-matrix metadata (`fieldPolicies`).  Only values
// tracked by the selected Leave Type are rendered.  This keeps the view modern
// and compact without hiding the accounting logic that HR needs to audit.

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchLeaveBalances, saveLeaveBalanceAdjustments, resetLeaveYearSummaries } from "../../services/vacationSettingsService";
import { isRequestCanceled } from "../../services/httpUtils";

/* -------------------------------------------------------------------------- */
/* Formatting helpers                                                         */
/* -------------------------------------------------------------------------- */

function fmt2(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function unitLabel(unit) {
  const u = String(unit || "DAYS").toUpperCase();
  return u === "HOURS" ? "h" : "d";
}

/**
 * Convert a row value into hours for the small work-time net summary.
 *
 * The annual summary table can store values in the unit configured on the
 * Leave Type.  Operational work-time types should normally be configured in
 * HOURS, but this helper is intentionally defensive: if a legacy/test Leave
 * Type is still configured as DAYS, we convert by the hb_visits full-time
 * equivalent rule: 1 day = 8 hours.
 */
function summaryValueToHours(value, unit) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;

  const u = String(unit || "DAYS").toUpperCase();
  if (u === "HOURS") return n;

  return n * 8;
}

function formatHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0.00 h";
  return `${n.toFixed(2)} h`;
}

function formatSignedHours(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "0.00 h";
  const sign = n > 0 ? "+" : "-";
  return `${sign}${Math.abs(n).toFixed(2)} h`;
}

function getBucketBreakdown(row) {
  return row?.final?.balance_buckets || row?.balance_buckets || null;
}

function getCarryoverBucket(row) {
  return getBucketBreakdown(row)?.opening_balance || null;
}

function getCurrentEntitlementBucket(row) {
  return getBucketBreakdown(row)?.current_entitlement || null;
}

function asBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "y";
}

function normalizeCode(code) {
  return String(code || "").trim().toLowerCase();
}

function normalizeAccountingType(lt) {
  const raw = String(lt?.balance_accounting_type || "").trim().toUpperCase();
  if (raw) return raw;

  return "NO_BALANCE_EFFECT";
}


function buildLeaveTypeMaps(leaveTypes, translate) {
  const byId = new Map();
  const nameById = new Map();

  (Array.isArray(leaveTypes) ? leaveTypes : []).forEach((t) => {
    const id = Number(t.id);
    const fallbackName = translate("leaves:balances.leaveTypeFallback", { id });

    byId.set(id, t);
    nameById.set(id, t.name_pl || t.name_en || t.name || t.code || fallbackName);
  });

  return {
    getType: (id) => byId.get(Number(id)) || null,
    getName: (id) => nameById.get(Number(id)) || translate("leaves:balances.leaveTypeFallback", { id }),
  };
}

/**
 * Build row policy from leave_types + fieldPolicies.
 *
 * v16 design decision
 * -------------------
 * Annual-summary field rules are no longer stored as `tracks_*` columns on
 * leave_types.  They come from leave_type_year_summary_field_policies and are
 * returned by the backend as `fieldPolicies`.  The frontend uses them to render
 * checkmarks and to build the correction dropdown dynamically.
 */
function getPolicies(lt) {
  return Array.isArray(lt?.fieldPolicies)
    ? lt.fieldPolicies
    : Array.isArray(lt?.field_policies)
      ? lt.field_policies
      : [];
}

function getPolicy(lt, fieldKey) {
  return getPolicies(lt).find((p) => String(p.field_key).toUpperCase() === fieldKey) || null;
}

function policyTracks(lt, fieldKey) {
  return asBool(getPolicy(lt, fieldKey)?.tracks_value);
}

function policyAdjusts(lt, fieldKey) {
  const p = getPolicy(lt, fieldKey);
  return asBool(p?.tracks_value) && asBool(p?.can_adjust_value);
}

function buildRulesFromLeaveType(lt) {
  const accountingType = normalizeAccountingType(lt);
  const policies = getPolicies(lt);

  return {
    show_opening_balance: policyTracks(lt, "OPENING_BALANCE"),
    show_entitlement: policyTracks(lt, "ENTITLEMENT"),
    show_annual_limit: policyTracks(lt, "ANNUAL_LIMIT"),
    show_deducted: policyTracks(lt, "DEDUCTED"),
    show_paid_out: policyTracks(lt, "PAID_OUT"),
    show_forfeited: policyTracks(lt, "FORFEITED"),
    show_taken: policyTracks(lt, "TAKEN"),
    show_planned: policyTracks(lt, "PLANNED"),
    show_available: policyTracks(lt, "AVAILABLE"),
    show_usage: policyTracks(lt, "TAKEN") || policyTracks(lt, "PLANNED"),

    can_adjust_opening_balance: policyAdjusts(lt, "OPENING_BALANCE"),
    can_adjust_entitlement: policyAdjusts(lt, "ENTITLEMENT"),
    can_adjust_annual_limit: policyAdjusts(lt, "ANNUAL_LIMIT"),
    can_adjust_deducted: policyAdjusts(lt, "DEDUCTED"),
    can_adjust_paid_out: policyAdjusts(lt, "PAID_OUT"),
    can_adjust_forfeited: policyAdjusts(lt, "FORFEITED"),
    can_adjust_taken: policyAdjusts(lt, "TAKEN"),
    can_adjust_planned: policyAdjusts(lt, "PLANNED"),
    can_adjust_available: policyAdjusts(lt, "AVAILABLE"),

    code: normalizeCode(lt?.code),
    unit: lt?.unit || "DAYS",
    balance_accounting_type: accountingType,
    balance_consumption_strategy: String(lt?.balance_consumption_strategy || "NONE"),
    uses_balance_buckets: asBool(lt?.uses_balance_buckets) || String(lt?.balance_consumption_strategy || "NONE").toUpperCase() !== "NONE",
    year_transition_mode: String(getPolicy(lt, "OPENING_BALANCE")?.year_transition_mode || "NONE"),
    show_in_calendar: asBool(lt?.show_in_calendar),
    is_paid: asBool(lt?.is_paid),
    requires_approval: asBool(lt?.requires_approval),
    supports_partial_day: asBool(lt?.supports_partial_day),
    fieldPolicies: policies,
  };
}

function PolicyBadge({ children, muted = false, title = "" }) {
  return (
    <span
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 999,
        padding: "2px 7px",
        margin: "3px 4px 0 0",
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.35,
        background: muted ? "#f8f9fb" : "#fff8d8",
        color: muted ? "#667085" : "#344054",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Adjustment choices                                                         */
/* -------------------------------------------------------------------------- */

const ADJUST_FIELDS = [
  { key: "OPENING_BALANCE", rule: "can_adjust_opening_balance" },
  { key: "ENTITLEMENT", rule: "can_adjust_entitlement" },
  { key: "ANNUAL_LIMIT", rule: "can_adjust_annual_limit" },
  { key: "PAID_OUT", rule: "can_adjust_paid_out" },
  { key: "FORFEITED", rule: "can_adjust_forfeited" },
  { key: "DEDUCTED", rule: "can_adjust_deducted" },
  { key: "TAKEN", rule: "can_adjust_taken" },
  { key: "PLANNED", rule: "can_adjust_planned" },
  { key: "AVAILABLE", rule: "can_adjust_available" },
];

const ADJUST_MODES = [
  { key: "DELTA" },
  { key: "OVERRIDE" },
];

export default function VacationBalancesSection({ userId, leaveTypes, readOnly = false }) {
  const { t } = useTranslation(["common", "leaves"]);
  const isReadOnly = !!readOnly;
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [includePending, setIncludePending] = useState(true);

  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [dirty, setDirty] = useState(() => new Set());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /**
   * Presentation model
   * ------------------
   * The first bucket-aware implementation used one very wide table.  That was
   * technically transparent, but it overloaded the User Settings card because
   * every bucket metric had to become another physical column.
   *
   * The current design intentionally uses modern balance cards instead:
   * - one card per Leave Type,
   * - headline values remain immediately visible,
   * - carryover/current-year details are grouped into two compact panels,
   * - HR correction controls stay close to the affected Leave Type,
   * - no horizontal scrolling is required for normal laptop/mobile widths.
   *
   * This is a local component-level change.  It avoids widening shared layout
   * containers and therefore does not affect Dashboard shell, admin overview
   * pages, public shop pages, pagination gutters, or other table-heavy screens.
   */
  const { getType, getName } = useMemo(() => buildLeaveTypeMaps(leaveTypes, t), [leaveTypes, t]);

  const rulesByTypeId = useMemo(() => {
    const map = new Map();
    (Array.isArray(leaveTypes) ? leaveTypes : []).forEach((lt) => {
      map.set(Number(lt.id), buildRulesFromLeaveType(lt));
    });
    return map;
  }, [leaveTypes]);

  function getRules(leaveTypeId) {
    return rulesByTypeId.get(Number(leaveTypeId)) || buildRulesFromLeaveType(null);
  }

  function allowedFieldsForRow(row) {
    const rules = getRules(row?.leave_type_id);
    return ADJUST_FIELDS.filter((f) => !!rules[f.rule]);
  }

  const visibleRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const nameA = getName(a.leave_type_id);
      const nameB = getName(b.leave_type_id);
      return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
    });
  }, [rows, getName]);

  /**
   * Small net summary for work-time debt.
   *
   * v11 design decision
   * -------------------
   * After v10, all Leave Types are visible again in one annual summary table
   * and all corrections are controlled by leave_types policy flags.  Therefore
   * we no longer need a second editable WorkTimeBalanceSection.
   *
   * However, PRIVATE_EXIT_TO_MAKE_UP and MAKE_UP_WORK are two different Leave
   * Types that together represent one practical business question:
   * "Does the employee still have time to make up, or do they have a time
   * credit?"
   *
   * This memo calculates that answer from the same annual-summary rows that are
   * already displayed in the unified table.  It is intentionally read-model
   * only: no separate correction form, no separate endpoint, no duplicate
   * balance mechanism.
   */
  const workTimeNetSummary = useMemo(() => {
    let hasWorkTimeRows = false;
    let debtHours = 0;
    let creditHours = 0;

    visibleRows.forEach((row) => {
      const rules = getRules(row.leave_type_id);
      const accounting = rules.balance_accounting_type;

      if (accounting !== "ADD_TIME_DEBT" && accounting !== "REDUCE_TIME_DEBT") {
        return;
      }

      hasWorkTimeRows = true;

      const fin = row.final || {};
      const unit = row.unit || rules.unit || "HOURS";

      // The annual summary row has one generic opening-balance field and then
      // usage/correction fields.  For ADD_TIME_DEBT rows these values increase
      // the amount the employee still has to make up.  For REDUCE_TIME_DEBT rows
      // the same fields increase the credit/make-up side.
      const totalHours =
        summaryValueToHours(fin.opening_balance ?? fin.carryover, unit) +
        summaryValueToHours(fin.deducted, unit) +
        summaryValueToHours(fin.taken, unit) +
        summaryValueToHours(fin.planned, unit);

      if (accounting === "ADD_TIME_DEBT") {
        debtHours += totalHours;
      } else {
        creditHours += totalHours;
      }
    });

    // Owner/operator presentation rule:
    //   PRIVATE_EXIT_TO_MAKE_UP = negative balance impact
    //   MAKE_UP_WORK            = positive balance impact
    //
    // The backend service can still expose debt-first values internally; this
    // UI summary deliberately shows the practical work-time balance that the
    // salon owner expects to see.
    const privateExitImpactHours = -debtHours;
    const makeUpWorkImpactHours = creditHours;
    const netHours = makeUpWorkImpactHours + privateExitImpactHours;

    return {
      hasWorkTimeRows,
      debtHours,
      creditHours,
      privateExitImpactHours,
      makeUpWorkImpactHours,
      netHours,
    };
    // getRules is stable enough for render-time use here because it reads the
    // memoized rulesByTypeId map.  visibleRows changes whenever rows reload.
   
  }, [visibleRows, rulesByTypeId]);

  /* ------------------------------------------------------------------------ */
  /* Load annual summaries                                                    */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const data = await fetchLeaveBalances(userId, year, { includePending, signal: ac.signal });
        const rawList = Array.isArray(data) ? data : [];
        setRows(rawList);

        setDraft((prev) => {
          const next = { ...(prev || {}) };

          for (const r of rawList) {
            const k = String(r.leave_type_id);
            const allowed = allowedFieldsForRow(r);
            const allowedKeys = new Set(allowed.map((x) => x.key));
            const defaultField = allowed?.[0]?.key || "DEDUCTED";

            if (!next[k]) {
              next[k] = { field: defaultField, mode: "DELTA", value: "0.00", note: "" };
              continue;
            }

            const chosen = next[k]?.field || defaultField;
            next[k] = {
              field: allowedKeys.has(chosen) ? chosen : defaultField,
              mode: next[k]?.mode || "DELTA",
              value: next[k]?.value ?? "0.00",
              note: next[k]?.note ?? "",
            };
          }

          return next;
        });
      } catch (e) {
        if (isRequestCanceled(e)) return;
        console.error("[VacationBalancesSection] ❌ load failed:", e);
        setError(t("leaves:balances.loadError"));
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();

  }, [userId, year, includePending, leaveTypes]);

  /* ------------------------------------------------------------------------ */
  /* Draft handling                                                           */
  /* ------------------------------------------------------------------------ */

  const patchDraft = (leaveTypeId, patch) => {
    const key = String(leaveTypeId);

    setDraft((prev) => {
      const cur = prev?.[key] || {};
      const safe = {
        field: cur.field || "DEDUCTED",
        mode: cur.mode || "DELTA",
        value: cur.value ?? "0.00",
        note: cur.note ?? "",
      };
      return { ...(prev || {}), [key]: { ...safe, ...(patch || {}) } };
    });

    setDirty((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const resetRow = (leaveTypeId) => {
    const key = String(leaveTypeId);

    setDraft((prev) => {
      const next = { ...(prev || {}) };
      delete next[key];
      return next;
    });

    setDirty((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const saveCorrections = async () => {
    if (isReadOnly) return;

    const adjustments = Array.from(dirty)
      .map((key) => {
        const row = visibleRows.find((r) => String(r.leave_type_id) === key);
        if (!row) return null;

        const d = draft?.[key];
        if (!d) return null;

        const allowed = allowedFieldsForRow(row).map((x) => x.key);
        if (!allowed.includes(d.field)) return null;

        return {
          leave_type_id: Number(row.leave_type_id),
          field: d.field,
          mode: d.mode || "DELTA",
          value: d.value ?? "0.00",
          reason: d.note || null,
        };
      })
      .filter(Boolean);

    if (!adjustments.length) {
      setError(t("leaves:balances.nothingToSave"));
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const updated = await saveLeaveBalanceAdjustments(userId, year, adjustments, { includePending });
      const list = Array.isArray(updated) ? updated : [];
      setRows(list);
      setDirty(new Set());
      setSuccess(t("leaves:balances.saved"));
    } catch (e) {
      console.error("[VacationBalancesSection] ❌ save failed:", e);
      setError(e?.response?.data?.message || t("leaves:balances.saveError"));
    } finally {
      setSaving(false);
    }
  };



  const resetAnnualSummary = async () => {
    if (isReadOnly) return;

    const ok = window.confirm(
      t("leaves:balances.resetYearConfirm", {
        year,
        defaultValue: `Delete annual summary and manual corrections for ${year}, then rebuild from current Leave Type policies and requests? Leave requests will not be deleted.`,
      })
    );

    if (!ok) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const updated = await resetLeaveYearSummaries(userId, year, { includePending });
      const list = Array.isArray(updated) ? updated : [];
      setRows(list);
      setDraft({});
      setDirty(new Set());
      setSuccess(t("leaves:balances.resetYearSuccess", { year, defaultValue: `Annual summary for ${year} was reset and rebuilt.` }));
    } catch (e) {
      console.error("[VacationBalancesSection] ❌ reset annual summary failed:", e);
      setError(e?.response?.data?.message || t("leaves:balances.resetYearError", { defaultValue: "Failed to reset annual summary." }));
    } finally {
      setSaving(false);
    }
  };

  function renderMetricTile({ label, value, relevant, unit = null, strong = false, help = "" }) {
    if (!relevant) return null;

    return (
      <div
        title={help || t("leaves:balances.relevantColumn", { defaultValue: "Relevant for this Leave Type" })}
        style={{
          minWidth: 0,
          border: strong ? "1px solid rgba(29, 78, 216, 0.22)" : "1px solid rgba(16, 24, 40, 0.09)",
          borderRadius: 14,
          padding: "10px 12px",
          background: strong ? "#eef4ff" : "#ffffff",
          boxShadow: strong ? "0 8px 20px rgba(29, 78, 216, 0.06)" : "0 4px 12px rgba(16, 24, 40, 0.035)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: "#667085",
            textTransform: "uppercase",
            letterSpacing: ".04em",
            marginBottom: 5,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: strong ? 20 : 16,
            fontWeight: strong ? 900 : 800,
            color: strong ? "#1d4ed8" : "#101828",
            whiteSpace: "nowrap",
          }}
        >
          {fmt2(value)}{unit ? ` ${unit}` : ""}
        </div>
      </div>
    );
  }

  function renderBucketPanel({ title, subtitle, bucketTone, unit, metrics }) {
    const visibleMetrics = (metrics || []).filter((m) => m.relevant);
    if (!visibleMetrics.length) return null;

    return (
      <div
        style={{
          border: "1px solid rgba(16, 24, 40, 0.10)",
          borderRadius: 16,
          padding: 12,
          background: bucketTone === "carryover" ? "#fffaf0" : "#f8fbff",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 900, color: "#101828" }}>{title}</div>
            {subtitle && <div className="vac-small" style={{ marginTop: 2, color: "#667085" }}>{subtitle}</div>}
          </div>
          <span
            style={{
              borderRadius: 999,
              padding: "3px 8px",
              fontSize: 11,
              fontWeight: 800,
              color: "#475467",
              background: "rgba(255,255,255,0.76)",
              border: "1px solid rgba(16, 24, 40, 0.08)",
              whiteSpace: "nowrap",
            }}
          >
            {unit}
          </span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: 8,
          }}
        >
          {visibleMetrics.map((m) => (
            <div key={m.key} style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#667085", fontWeight: 800, marginBottom: 3 }}>{m.label}</div>
              <div style={{ fontSize: 15, color: m.strong ? "#1d4ed8" : "#101828", fontWeight: m.strong ? 900 : 800 }}>
                {fmt2(m.value)} {unit}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="vac-section-card">
      <div className="section-head">
        <div>
          <h3>{t("leaves:balances.title")}</h3>
          <p>{t("leaves:balances.subtitleUnified", {
            defaultValue: "Annual summary for all Leave Types. Checkmarks indicate values tracked for each Leave Type. Policy badges are shown in the sticky Leave Type column.",
          })}</p>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <div className="vac-grid-compact">
        <div className="vac-field">
          <label>{t("leaves:balances.year")}</label>
          <input
            type="number"
            min="2000"
            max="2100"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            disabled={loading || saving}
          />
        </div>

        <div className="vac-field">
          <label>{t("leaves:balances.plannedIncludesPending")}</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={includePending}
              onChange={(e) => setIncludePending(e.target.checked)}
              disabled={loading || saving}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontWeight: 700 }}>
              {includePending ? t("leaves:balances.approvedPlusPending") : t("leaves:balances.approvedOnly")}
            </span>
          </div>
        </div>
      </div>

      {loading ? (
        <p>{t("leaves:balances.loading")}</p>
      ) : rows.length === 0 ? (
        <p style={{ marginTop: 10 }}>{t("leaves:balances.noBalances", { year })}</p>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 14,
              marginTop: 14,
            }}
          >
            {visibleRows.map((r) => {
              const k = String(r.leave_type_id);
              const fin = r.final || {};
              const lt = getType(r.leave_type_id);
              const rules = getRules(r.leave_type_id);
              const rowIsDirty = dirty.has(k);
              const allowedFields = allowedFieldsForRow(r);
              const allowedKeys = new Set(allowedFields.map((x) => x.key));
              const d = draft?.[k] || {
                field: allowedFields?.[0]?.key || "DEDUCTED",
                mode: "DELTA",
                value: "0.00",
                note: "",
              };

              const fieldIsAllowed = allowedKeys.has(d.field);
              const rowEditable = allowedFields.length > 0;
              const unit = unitLabel(r.unit || rules.unit);
              const openingValue = fin.opening_balance ?? fin.carryover;
              const carryoverBucket = getCarryoverBucket(r);
              const currentBucket = getCurrentEntitlementBucket(r);
              const bucketStrategy = String(rules.balance_consumption_strategy || fin.balance_buckets?.strategy || "NONE").toUpperCase();
              const hasCarryoverBucket = !!carryoverBucket || bucketStrategy === "CARRYOVER_FIRST" || bucketStrategy === "CURRENT_YEAR_FIRST";
              const hasCurrentBucket = !!currentBucket || bucketStrategy === "CARRYOVER_FIRST" || bucketStrategy === "CURRENT_YEAR_FIRST";
              const takenRelevant = rules.show_taken;
              const plannedRelevant = rules.show_planned;

              const primaryMetrics = [
                {
                  key: "available",
                  label: t("leaves:balances.headers.available"),
                  value: fin.available,
                  relevant: rules.show_available,
                  strong: true,
                },
                {
                  key: "taken",
                  label: t("leaves:balances.headers.taken"),
                  value: fin.taken,
                  relevant: takenRelevant,
                },
                {
                  key: "planned",
                  label: t("leaves:balances.headers.planned"),
                  value: fin.planned,
                  relevant: plannedRelevant,
                },
                {
                  key: "openingBalance",
                  label: t("leaves:balances.headers.openingBalance"),
                  value: openingValue,
                  relevant: rules.show_opening_balance,
                },
                {
                  key: "entitlement",
                  label: t("leaves:balances.headers.entitlement"),
                  value: fin.entitlement,
                  relevant: rules.show_entitlement,
                },
                {
                  key: "annualLimit",
                  label: t("leaves:balances.headers.annualLimit", { defaultValue: "Annual limit" }),
                  value: fin.annual_limit,
                  relevant: rules.show_annual_limit,
                },
              ];

              const secondaryMetrics = [
                {
                  key: "deducted",
                  label: t("leaves:balances.headers.deducted"),
                  value: fin.deducted,
                  relevant: rules.show_deducted,
                },
                {
                  key: "paidOut",
                  label: t("leaves:balances.headers.paidOut"),
                  value: fin.paid_out,
                  relevant: rules.show_paid_out,
                },
                {
                  key: "forfeited",
                  label: t("leaves:balances.headers.forfeited"),
                  value: fin.forfeited,
                  relevant: rules.show_forfeited,
                },
              ];

              return (
                <article
                  key={`${r.leave_type_id}-${r.year}`}
                  className={rowIsDirty ? "vac-dirty" : ""}
                  style={{
                    border: rowIsDirty ? "1px solid #fedf89" : "1px solid rgba(16, 24, 40, 0.10)",
                    borderRadius: 20,
                    padding: 16,
                    background: rowIsDirty ? "#fffaf0" : "#ffffff",
                    boxShadow: "0 10px 30px rgba(16, 24, 40, 0.055)",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                      gap: 16,
                      alignItems: "start",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 900, fontSize: 16, color: "#101828" }}>{getName(r.leave_type_id)}</div>
                      {lt && (
                        <>
                          <div className="vac-small" style={{ marginTop: 4, opacity: 0.7 }}>
                            {t("leaves:balances.codeLabel")}: <b>{normalizeCode(lt.code)}</b>
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <PolicyBadge title={rules.balance_accounting_type}>
                              {t(`leaves:leaveTypes.accountingTypesShort.${rules.balance_accounting_type}`, { defaultValue: rules.balance_accounting_type })}
                            </PolicyBadge>
                            <PolicyBadge muted>{String(r.unit || rules.unit || "DAYS")}</PolicyBadge>
                            {rules.requires_approval && <PolicyBadge>{t("leaves:balances.badges.requiresApproval", { defaultValue: "Approval" })}</PolicyBadge>}
                            {rules.is_paid ? <PolicyBadge>{t("leaves:balances.badges.paid", { defaultValue: "Paid" })}</PolicyBadge> : <PolicyBadge muted>{t("leaves:balances.badges.unpaid", { defaultValue: "Unpaid" })}</PolicyBadge>}
                            {rules.supports_partial_day && <PolicyBadge>{t("leaves:balances.badges.partialDay", { defaultValue: "Partial day" })}</PolicyBadge>}
                            {rules.show_in_calendar && <PolicyBadge>{t("leaves:balances.badges.calendar", { defaultValue: "Calendar" })}</PolicyBadge>}
                            {rules.show_entitlement && <PolicyBadge>{t("leaves:balances.badges.userEntitlement", { defaultValue: "User entitlement" })}</PolicyBadge>}
                            {rules.show_annual_limit && <PolicyBadge>{t("leaves:balances.badges.annualLimit", { defaultValue: "Annual limit" })}</PolicyBadge>}
                            {rules.uses_balance_buckets && <PolicyBadge>{t(`leaves:balances.consumptionStrategiesShort.${String(rules.balance_consumption_strategy).toUpperCase()}`, { defaultValue: rules.balance_consumption_strategy })}</PolicyBadge>}
                            {rules.year_transition_mode !== "NONE" && <PolicyBadge>{t(`leaves:leaveTypes.yearTransitionModesShort.${rules.year_transition_mode}`, { defaultValue: rules.year_transition_mode })}</PolicyBadge>}
                          </div>
                        </>
                      )}
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))",
                          gap: 10,
                        }}
                      >
                        {[...primaryMetrics, ...secondaryMetrics].map((m) => (
                          <React.Fragment key={m.key}>
                            {renderMetricTile({ ...m, unit })}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>

                  {(hasCarryoverBucket || hasCurrentBucket) && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: 12,
                        marginTop: 14,
                      }}
                    >
                      {renderBucketPanel({
                        title: t("leaves:balances.bucketCards.carryoverTitle", { defaultValue: "Carryover / previous-year leave" }),
                        subtitle: t("leaves:balances.bucketCards.carryoverSubtitle", { defaultValue: "Consumed first when the Leave Type uses carryover-first accounting." }),
                        bucketTone: "carryover",
                        unit,
                        metrics: [
                          { key: "opening", label: t("leaves:balances.bucketCards.initial", { defaultValue: "Initial" }), value: openingValue, relevant: rules.show_opening_balance },
                          { key: "used", label: t("leaves:balances.bucketCards.used", { defaultValue: "Used" }), value: fin.opening_balance_used ?? carryoverBucket?.used_value, relevant: rules.show_opening_balance && hasCarryoverBucket },
                          { key: "planned", label: t("leaves:balances.bucketCards.planned", { defaultValue: "Planned" }), value: fin.opening_balance_planned ?? carryoverBucket?.planned_value, relevant: rules.show_opening_balance && hasCarryoverBucket },
                          { key: "remaining", label: t("leaves:balances.bucketCards.remaining", { defaultValue: "Remaining" }), value: fin.opening_balance_remaining ?? carryoverBucket?.remaining_value, relevant: rules.show_opening_balance && hasCarryoverBucket, strong: true },
                        ],
                      })}

                      {renderBucketPanel({
                        title: t("leaves:balances.bucketCards.currentTitle", { defaultValue: "Current-year entitlement" }),
                        subtitle: t("leaves:balances.bucketCards.currentSubtitle", { defaultValue: "Used after carryover, or first when configured by the Leave Type strategy." }),
                        bucketTone: "current",
                        unit,
                        metrics: [
                          { key: "entitlement", label: t("leaves:balances.bucketCards.initial", { defaultValue: "Initial" }), value: fin.entitlement, relevant: rules.show_entitlement },
                          { key: "used", label: t("leaves:balances.bucketCards.used", { defaultValue: "Used" }), value: fin.entitlement_used ?? currentBucket?.used_value, relevant: rules.show_entitlement && hasCurrentBucket },
                          { key: "planned", label: t("leaves:balances.bucketCards.planned", { defaultValue: "Planned" }), value: fin.entitlement_planned ?? currentBucket?.planned_value, relevant: rules.show_entitlement && hasCurrentBucket },
                          { key: "remaining", label: t("leaves:balances.bucketCards.remaining", { defaultValue: "Remaining" }), value: fin.entitlement_remaining ?? currentBucket?.remaining_value, relevant: rules.show_entitlement && hasCurrentBucket, strong: true },
                        ],
                      })}
                    </div>
                  )}

                  {!isReadOnly && (
                    <div
                      style={{
                        marginTop: 14,
                        paddingTop: 14,
                        borderTop: "1px dashed rgba(16, 24, 40, 0.14)",
                      }}
                    >
                      <div style={{ fontWeight: 900, marginBottom: 8, color: "#344054" }}>
                        {t("leaves:balances.correctionSectionTitle", { defaultValue: "HR correction" })}
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <select
                          value={fieldIsAllowed ? d.field : (allowedFields?.[0]?.key || "")}
                          onChange={(e) => patchDraft(r.leave_type_id, { field: e.target.value })}
                          disabled={!rowEditable || loading || saving}
                          style={{ width: "100%", padding: 10, borderRadius: 8 }}
                          title={!rowEditable ? t("leaves:balances.noCorrectionsTitle") : undefined}
                        >
                          {allowedFields.length === 0 ? (
                            <option value="">{t("leaves:balances.noCorrectionsAllowed")}</option>
                          ) : (
                            allowedFields.map((f) => (
                              <option key={f.key} value={f.key}>
                                {t(`leaves:balances.fields.${f.key}`, { defaultValue: f.key })}
                              </option>
                            ))
                          )}
                        </select>

                        <select
                          value={d.mode}
                          onChange={(e) => patchDraft(r.leave_type_id, { mode: e.target.value })}
                          disabled={!rowEditable || loading || saving}
                          style={{ width: "100%", padding: 10, borderRadius: 8 }}
                        >
                          {ADJUST_MODES.map((m) => (
                            <option key={m.key} value={m.key}>
                              {t(`leaves:balances.modes.${m.key}`, { defaultValue: m.key })}
                            </option>
                          ))}
                        </select>

                        <input
                          type="number"
                          step="0.01"
                          value={d.value}
                          onChange={(e) => patchDraft(r.leave_type_id, { value: e.target.value })}
                          disabled={!rowEditable || loading || saving}
                          style={{ width: "100%", minWidth: 96, boxSizing: "border-box", textAlign: "right" }}
                        />

                        <input
                          type="text"
                          value={d.note}
                          onChange={(e) => patchDraft(r.leave_type_id, { note: e.target.value })}
                          disabled={!rowEditable || loading || saving}
                          placeholder={t("leaves:balances.optionalPlaceholder")}
                          style={{ width: "100%", textAlign: "left" }}
                        />

                        <button
                          type="button"
                          className="mode-btn"
                          onClick={() => resetRow(r.leave_type_id)}
                          disabled={!rowIsDirty || loading || saving}
                          title={t("leaves:balances.discardRowTitle")}
                        >
                          {t("common:actions.reset")}
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          <p className="tiers-hint" style={{ marginTop: 10 }}>
            {t("leaves:balances.modernCardsHint", {
              defaultValue: "Annual balances are displayed as compact cards instead of one very wide table. Main totals stay visible first; bucket-aware vacation rows show carryover and current-year entitlement as separate panels.",
            })}
            {" "}
            {t("leaves:balances.bucketHint", {
              defaultValue: "For bucket-aware vacation rows, taken/planned usage is split into carryover and current-year entitlement so cancellation can restore the exact source bucket.",
            })}
          </p>

          {workTimeNetSummary.hasWorkTimeRows && (
            <div className="vac-card" style={{ marginTop: 14, boxShadow: "none" }}>
              <div className="vac-card-title">
                {t("leaves:balances.workTimeNet.title", { defaultValue: "Work time balance summary" })}
              </div>

              <p className="section-subtitle">
                {t("leaves:balances.workTimeNet.help", {
                  defaultValue: "Net summary calculated from the same annual Leave Type rows above. Corrections remain part of the unified table; this box is only a transparent read-only total.",
                })}
              </p>

              <div className="vac-grid" style={{ marginTop: 10 }}>
                <div className="vac-field">
                  <label>{t("leaves:balances.workTimeNet.debt", { defaultValue: "Private exits" })}</label>
                  <div
                    className="settings-value-pill"
                    title={t("leaves:balances.workTimeNet.privateExitHelp", {
                      defaultValue: "Private exits decrease the employee work-time balance.",
                    })}
                  >
                    {formatSignedHours(workTimeNetSummary.privateExitImpactHours)}
                  </div>
                </div>

                <div className="vac-field">
                  <label>{t("leaves:balances.workTimeNet.credit", { defaultValue: "Make-up work" })}</label>
                  <div
                    className="settings-value-pill"
                    title={t("leaves:balances.workTimeNet.makeUpHelp", {
                      defaultValue: "Make-up work increases the employee work-time balance.",
                    })}
                  >
                    {formatSignedHours(workTimeNetSummary.makeUpWorkImpactHours)}
                  </div>
                </div>

                <div className="vac-field">
                  <label>{t("leaves:balances.workTimeNet.net", { defaultValue: "Net balance" })}</label>
                  <div className="settings-value-pill">{formatSignedHours(workTimeNetSummary.netHours)}</div>
                </div>
              </div>

              <p className="tiers-hint" style={{ marginTop: 8 }}>
                {workTimeNetSummary.netHours < 0
                  ? t("leaves:balances.workTimeNet.toMakeUp", { defaultValue: "Negative net balance means the employee still has time to make up." })
                  : workTimeNetSummary.netHours > 0
                    ? t("leaves:balances.workTimeNet.creditHint", { defaultValue: "Positive net balance means the employee has a work-time credit." })
                    : t("leaves:balances.workTimeNet.balanced", { defaultValue: "The work-time balance is currently settled." })}
              </p>
            </div>
          )}
        </>
      )}

      {!isReadOnly && (
        <div className="vac-save-row" style={{ gap: 10 }}>
          <button
            type="button"
            className="primary"
            onClick={saveCorrections}
            disabled={saving || loading || rows.length === 0 || dirty.size === 0}
          >
            {saving
              ? t("leaves:vacationSettings.saving")
              : (dirty.size
                ? t("leaves:balances.saveCorrectionsWithCount", { count: dirty.size })
                : t("leaves:balances.saveCorrections"))}
          </button>

          <button
            type="button"
            className="mode-btn"
            onClick={() => setDirty(new Set())}
            disabled={saving || loading || dirty.size === 0}
            title={t("leaves:balances.discardAllTitle")}
          >
            {t("leaves:balances.discardAll")}
          </button>

          <button
            type="button"
            className="mode-btn"
            onClick={resetAnnualSummary}
            style={{ borderColor: "#fecdca", color: "#b42318", background: "#fff8f7" }}
            disabled={saving || loading}
            title={t("leaves:balances.resetYearTitle", {
              defaultValue: "Delete annual summary rows and manual corrections for this user/year, then rebuild from requests and current Leave Type policies.",
            })}
          >
            {t("leaves:balances.resetYear", { defaultValue: "Reset annual summary" })}
          </button>
        </div>
      )}
    </div>
  );
}


